import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Key, FlaskConical,
  ScrollText, Settings,
  Atom, ChevronLeft, ChevronDown,
  Wallet, Layers, Boxes, Users,
  Globe, Sun, Moon, FlaskRound, FileText, Receipt, FileClock, TrendingUp, Brain,
  ShieldCheck, LogOut, Mail, MailCheck,
  Activity, Landmark, PieChart, Package, ArrowLeftRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { ConsoleLayoutContext, type ConsoleHeaderMeta } from "./ConsoleLayoutContext";

type NavItem = { label: string; href: string; icon: React.ReactNode; badge?: string };
type AdminNavGroup = { id: string; label: string; icon: React.ReactNode; items: NavItem[] };

// Persistent console chrome (sidebar + header). Mounted ONCE above the console
// routes so navigation between console pages swaps only <main> content and the
// sidebar/header do not remount (previously every page re-rendered its own
// ConsoleLayout, causing a full-page spinner + sidebar flash on each nav).
export default function ConsoleShell({ children }: { children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  // Narrow viewports (below the 1024px breakpoint) start with the 60px icon
  // rail: the fixed 240px sidebar would otherwise leave the content area too
  // narrow for the cards. Crossing the breakpoint re-applies the matching
  // default; a manual toggle stays in effect until the viewport actually
  // changes, so the user's choice is never overridden in place.
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const handleChange = (event: MediaQueryListEvent) => setCollapsed(event.matches);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);
  const [meta, setMetaState] = useState<ConsoleHeaderMeta>({});
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { t, language, setLanguage } = useLanguage();
  const { platformTheme, setPlatformTheme } = useTheme();
  const { user, logout } = useAuth();

  const isTeamAdmin = user?.role === "team_admin" || user?.is_system_admin;
  const inAdminWorkspace = location.startsWith("/console/admin");

  // Reference-compare all three fields so actions updates propagate to the header
  // (button label/disabled changes when state like `exporting` toggles). No loop:
  // the shell's `routes` child is a stable ref from Router, so a shell re-render
  // does not cascade back into a page re-render.
  const setMeta = useCallback((m: ConsoleHeaderMeta) => {
    setMetaState((prev) =>
      prev.title === m.title && prev.subtitle === m.subtitle && prev.actions === m.actions
        ? prev
        : m
    );
  }, []);

  const ctxValue = useMemo(() => ({ setMeta }), [setMeta]);

  const SIDEBAR_SECTIONS = useMemo(() => [
    {
      title: t("console.overview"),
      items: [
        { label: t("console.dashboard"), href: "/console/dashboard", icon: <LayoutDashboard size={15} /> },
        { label: t("console.apiKeys"), href: "/console/keys", icon: <Key size={15} /> },
      ],
    },
    {
      title: t("console.sciModelLib"),
      items: [
        { label: t("console.sciLLMs"), href: "/console/llms", icon: <FlaskConical size={15} /> },
        { label: t("console.scienceAgentMarketplace"), href: "/console/science-agents", icon: <FlaskRound size={15} /> },
      ],
    },
    {
      title: t("console.account"),
      items: [
        { label: t("console.experimentLogs"), href: "/console/logs", icon: <ScrollText size={15} /> },
        { label: t("console.settings"), href: "/console/settings", icon: <Settings size={15} /> },
      ],
    },
    ...(isTeamAdmin
      ? [
          {
            title: t("console.teamGroup"),
            items: [
              { label: t("console.teamDashboard"), href: "/console/team/dashboard", icon: <LayoutDashboard size={15} /> },
              { label: t("console.teamManage"), href: "/console/team/members", icon: <Users size={15} /> },
              { label: t("console.teamBills"), href: "/console/team/bills", icon: <Receipt size={15} /> },
              { label: t("console.teamUsage"), href: "/console/team/usage", icon: <ScrollText size={15} /> },
              { label: t("console.teamWalletTransactions"), href: "/console/team/wallet-transactions", icon: <Receipt size={15} /> },
            ],
          },
        ]
      : []),
  // t is recreated on language change; the boolean gates the team section.
  ], [t, isTeamAdmin]);

  // Admin area: direct links + collapsible functional groups.
  const ADMIN_DIRECT = useMemo<NavItem[]>(() => [
    { label: t("console.adminDashboard"), href: "/console/admin/dashboard", icon: <LayoutDashboard size={15} /> },
    { label: t("console.adminTeams"), href: "/console/admin/teams", icon: <Wallet size={15} /> },
  ], [t]);

  const ADMIN_GROUPS = useMemo<AdminNavGroup[]>(() => [
    {
      id: "monitoring",
      label: t("console.adminGroup.monitoring"),
      icon: <Activity size={15} />,
      items: [
        { label: t("console.adminUsage"), href: "/console/admin/usage", icon: <ScrollText size={14} /> },
        { label: t("console.adminUsageAttempts"), href: "/console/admin/usage-attempts", icon: <ScrollText size={14} /> },
      ],
    },
    {
      id: "finance",
      label: t("console.adminGroup.finance"),
      icon: <Landmark size={15} />,
      items: [
        { label: t("console.adminBills"), href: "/console/admin/bills", icon: <FileText size={14} /> },
        { label: t("console.adminWalletTransactions"), href: "/console/admin/wallet-transactions", icon: <Receipt size={14} /> },
      ],
    },
    {
      id: "analytics",
      label: t("console.adminGroup.analytics"),
      icon: <PieChart size={15} />,
      items: [
        { label: t("console.adminInsights"), href: "/console/admin/insights", icon: <Brain size={14} /> },
        { label: t("console.adminMargin"), href: "/console/admin/margin", icon: <TrendingUp size={14} /> },
      ],
    },
    {
      id: "models",
      label: t("console.adminGroup.models"),
      icon: <Package size={15} />,
      items: [
        { label: t("console.adminProviders"), href: "/console/admin/providers", icon: <Layers size={14} /> },
        { label: t("console.adminScienceLLMs"), href: "/console/admin/science-llms", icon: <Boxes size={14} /> },
      ],
    },
    {
      id: "system",
      label: t("console.adminGroup.system"),
      icon: <ShieldCheck size={15} />,
      items: [
        { label: t("console.adminAuditLogs"), href: "/console/admin/audit-logs", icon: <FileClock size={14} /> },
        { label: t("console.adminEmailConfig"), href: "/console/admin/email-config", icon: <Mail size={14} /> },
        { label: t("console.adminEmailStatus"), href: "/console/admin/email-status", icon: <MailCheck size={14} /> },
      ],
    },
  ], [t]);

  const isItemActive = (href: string) => location === href || location.startsWith(href + "/");

  const renderNavLink = (item: NavItem, indent = false) => {
    const isActive = isItemActive(item.href);
    const className = cn(
      "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all duration-150 mb-0.5",
      indent && "pl-8",
      isActive
        ? "bg-blue-500/15 text-blue-400 font-medium"
        : "text-slate-500 hover:text-slate-200 hover:bg-white/5"
    );
    return (
      <Link key={item.href} href={item.href}
        className={className} title={collapsed ? item.label : undefined}>
        <span className={cn("flex-shrink-0", isActive ? "text-blue-400" : "")}>
          {item.icon}
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {typeof item.badge === "string" && (
              <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 leading-none">
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  return (
    <ConsoleLayoutContext.Provider value={ctxValue}>
      <div className="flex h-screen bg-background overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            "flex-shrink-0 flex flex-col border-r border-white/5 transition-all duration-300",
            collapsed ? "w-[60px]" : "w-[240px]"
          )}
          style={{ background: 'var(--console-sidebar-bg)' }}
        >
          {/* Logo */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-white/5">
            {!collapsed && (
              <Link href="/" className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 50%, #818CF8 100%)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v8l4 7H5l4-7V3z" /><path d="M9 3h6" /><circle cx="12" cy="15" r="1" fill="white" stroke="none" /></svg>
                </div>
                <span className="font-bold text-sm text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Science<span className="text-blue-400">Token</span>
                </span>
              </Link>
            )}
            {collapsed && (
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto">
                <Atom size={14} className="text-white" />
              </div>
            )}
            {!collapsed && (
              <button
                onClick={() => setCollapsed(true)}
                className="text-slate-500 hover:text-slate-300 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
            )}
          </div>

          {/* Nav Items — workspaces are mutually exclusive, matching the
              prototype: /console/admin/* shows ONLY the admin nav; the business
              console shows ONLY business + team sections. */}
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            {inAdminWorkspace ? (
              user?.is_system_admin && (
                <div className="mb-4">
                  {!collapsed && (
                    <div className="px-2 mb-1.5 text-[10px] font-semibold text-slate-600 tracking-widest">
                      {t("console.admin")}
                    </div>
                  )}
                  {collapsed ? (
                    [...ADMIN_DIRECT, ...ADMIN_GROUPS.flatMap((g) => g.items)].map((item) => renderNavLink(item))
                  ) : (
                    <>
                      {ADMIN_DIRECT.map((item) => renderNavLink(item))}
                      {ADMIN_GROUPS.map((group) => {
                        const isActiveGroup = group.items.some((item) => isItemActive(item.href));
                        // Untouched groups follow the active route (auto-expand);
                        // an explicit toggle overrides that default either way.
                        const isOpen = openGroups[group.id] ?? isActiveGroup;
                        return (
                          <div key={group.id}>
                            <button
                              onClick={() => setOpenGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                              className="flex items-center gap-2.5 px-2.5 py-2 w-full rounded-lg text-sm transition-all duration-150 mb-0.5 text-slate-500 hover:text-slate-200 hover:bg-white/5"
                            >
                              <span className="flex-shrink-0">{group.icon}</span>
                              <span className="flex-1 truncate text-left">{group.label}</span>
                              <ChevronDown size={14} className={cn("flex-shrink-0 transition-transform duration-200", isOpen && "rotate-180")} />
                            </button>
                            {isOpen && group.items.map((item) => renderNavLink(item, true))}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )
            ) : (
              SIDEBAR_SECTIONS.map((section) => (
                <div key={section.title} className="mb-4">
                  {!collapsed && (
                    <div className="px-2 mb-1.5 text-[10px] font-semibold text-slate-600 tracking-widest">
                      {section.title}
                    </div>
                  )}
                  {section.items.map((item) => renderNavLink(item))}
                </div>
              ))
            )}
          </nav>

          {/* User Profile */}
          <div className={cn(
            "border-t border-white/5 p-3",
            collapsed ? "flex justify-center" : ""
          )}>
            {!collapsed ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2.5 w-full rounded-lg p-1 -m-1 text-left hover:bg-white/5 transition-colors">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                      {user?.display_name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-300 truncate flex items-center gap-1.5">
                        {user?.display_name || "User"}
                        {user?.is_system_admin && (
                          <span title={t("admin.badge")} className="inline-flex items-center text-amber-400">
                            <ShieldCheck size={11} />
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-600 truncate">{user?.email || ""}</div>
                    </div>
                    <ChevronDown size={12} className="text-slate-600 flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-52">
                  {user?.is_system_admin && (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate(inAdminWorkspace ? "/console/dashboard" : "/console/admin/dashboard")}
                      >
                        <ArrowLeftRight className="size-3.5" />
                        {inAdminWorkspace ? t("console.workspace.backToConsole") : t("console.workspace.enterAdmin")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="size-3.5" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white">
                {user?.display_name?.charAt(0).toUpperCase() || "U"}
              </div>
            )}
          </div>

          {/* Expand button when collapsed */}
          {collapsed && (
            <button
              onClick={() => setCollapsed(false)}
              className="p-3 text-slate-500 hover:text-slate-300 border-t border-white/5 flex justify-center"
            >
              <ChevronLeft size={14} className="rotate-180" />
            </button>
          )}
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Header */}
          <header className="flex items-center justify-between h-14 px-6 border-b border-white/5 flex-shrink-0"
            style={{ background: 'var(--console-header-bg)' }}>
            <div>
              {meta.title && (
                <h1 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {meta.title}
                </h1>
              )}
              {meta.subtitle && <p className="text-xs text-slate-500">{meta.subtitle}</p>}
            </div>
            <div className="flex items-center gap-3">
              {meta.actions}
              {/* Language Switcher */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setLanguage(language === "zh" ? "en" : "zh")}
                    className="flex items-center gap-1 h-8 px-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all"
                    aria-label={language === "zh" ? "Switch to English" : "切换到中文"}
                  >
                    <Globe size={14} />
                    <span className="text-xs font-medium">{language === "zh" ? "中" : "EN"}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>{language === "zh" ? "Switch to English" : "切换到中文"}</TooltipContent>
              </Tooltip>
              {/* Theme Toggle */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setPlatformTheme(platformTheme === "tech" ? "fresh" : "tech")}
                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
                    aria-label={platformTheme === "tech" ? t("nav.switchToLight") : t("nav.switchToDark")}
                  >
                    {platformTheme === "tech" ? <Sun size={14} /> : <Moon size={14} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{platformTheme === "tech" ? t("nav.switchToLight") : t("nav.switchToDark")}</TooltipContent>
              </Tooltip>
              <Link href="/">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs gap-1.5">
                  {t("console.backToSite")}
                </Button>
              </Link>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </ConsoleLayoutContext.Provider>
  );
}
